import { css } from '@emotion/react'
import { Text, Theme, useTheme } from 'bold-ui'
import { keyframes } from 'emotion'
import { AnimationEvent, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { ToastPhase, VideochamadaToastModel } from './model-videochamadaToast'

interface VideochamadaToastItemProps {
  toast: VideochamadaToastModel
  onDismiss(id: string): void
}

const ANIMATION_DURATION = '300ms'
const EASING = 'cubic-bezier(0.16, 1, 0.3, 1)'
const SLIDE_OFFSET = '0.75rem'

export function VideochamadaToastItem(props: Readonly<VideochamadaToastItemProps>) {
  const { toast, onDismiss } = props
  const [phase, setPhase] = useState<ToastPhase>('entering')
  const theme = useTheme()
  const styles = useMemo(() => createStyles(theme, phase), [theme, phase])
  const visibleTimeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const rootRef = useRef<HTMLDivElement>(null)
  const prevTopRef = useRef<number>()
  const rafRef = useRef<number>()

  useEffect(() => {
    if (toast.dismissing) {
      visibleTimeoutRef.current && clearTimeout(visibleTimeoutRef.current)
      setPhase('leaving')
    }
  }, [toast.dismissing])

  useEffect(
    () => () => {
      visibleTimeoutRef.current && clearTimeout(visibleTimeoutRef.current)
      rafRef.current && cancelAnimationFrame(rafRef.current)
    },
    []
  )

  const forceReflow = (element: HTMLElement) => element.offsetHeight

  useLayoutEffect(() => {
    const el = rootRef.current
    if (!el) {
      return
    }

    const newTop = el.offsetTop
    const prevTop = prevTopRef.current
    prevTopRef.current = newTop

    if (phase !== 'visible' || prevTop === undefined || prevTop === newTop) {
      return
    }

    el.style.transition = 'none'
    el.style.transform = `translateY(${prevTop - newTop}px)`

    forceReflow(el)

    rafRef.current = requestAnimationFrame(() => {
      el.style.transition = `transform ${ANIMATION_DURATION} ${EASING}`
      el.style.transform = 'translateY(0)'
    })
  })

  const handleAnimationEnd = (event: AnimationEvent) => {
    if (event.target !== event.currentTarget) {
      return
    }

    if (phase === 'entering') {
      setPhase('visible')
      visibleTimeoutRef.current = setTimeout(() => setPhase('leaving'), toast.duration)
    } else if (phase === 'leaving') {
      onDismiss(toast.id)
    }
  }

  return (
    <div ref={rootRef} css={styles.card} onAnimationEnd={handleAnimationEnd} role='status'>
      {typeof toast.body === 'string' ? <Text style={styles.text}>{toast.body}</Text> : toast.body}
    </div>
  )
}
const enter = keyframes({
  from: { opacity: 0, transform: `translateY(${SLIDE_OFFSET})` },
  to: { opacity: 1, transform: 'translateY(0)' },
})

const leave = keyframes({
  from: { opacity: 1, transform: 'translateY(0)' },
  to: { opacity: 0, transform: `translateY(${SLIDE_OFFSET})` },
})

const getOpacity = (phase: ToastPhase) => {
  if (phase === 'entering') return 0
  return 1
}

const getAnimation = (phase: ToastPhase) => {
  if (phase === 'entering') return enter
  if (phase === 'leaving') return leave
  return 'none'
}

const createStyles = (theme: Theme, phase: ToastPhase) => ({
  card: css`
    min-width: 18rem;
    max-width: 100%;
    background-color: ${theme.pallete.gray.c20};
    box-shadow: ${theme.shadows.outer[40]};
    border-radius: 0.25rem;
    padding: 0.5rem;
    overflow-wrap: break-word;
    pointer-events: auto;
    opacity: ${getOpacity(phase)};
    animation: ${getAnimation(phase)} ${ANIMATION_DURATION} ${EASING} forwards;
  `,
  text: css`
    color: ${theme.pallete.gray.c100};
  `,
})
